import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { fetchTrainingOverview, type TrainingModulePlan } from '@features/Session';
import { trackEvent } from '@shared/analytics';
import { buildCramPath } from '@shared/config';

/** Сколько карточек берёт тренировка «На сегодня» (как на «Тренировке»). */
const TODAY_COUNT = 10;

type Translate = (key: string, options?: Record<string, unknown>) => string;

/** Короткая сводка модуля: что повторить, сколько ещё не учили или нечего — как в строках «Тренировки». */
export function lineStatus(
  plan: Pick<TrainingModulePlan, 'repeat' | 'ready' | 'new'>,
  t: Translate,
): { repeat: boolean; text: string } {
  if (plan.repeat > 0) return { repeat: true, text: t('words.mine.repeat', { count: plan.repeat }) };
  if (plan.ready === 0) return { repeat: false, text: t('training.rowEmpty') };
  if (plan.new > 0) return { repeat: false, text: t('training.rowUntrained', { count: plan.new }) };
  return { repeat: false, text: t('training.rowNothing') };
}

/** Сводка «что повторить» по модулям — общий кэш с «Тренировкой». */
export function useTrainingPlans() {
  const overview = useQuery({ queryKey: ['sessions', 'overview'], queryFn: fetchTrainingOverview, staleTime: 30_000 });
  return useMemo(() => new Map((overview.data ?? []).map((plan) => [plan.moduleId, plan])), [overview.data]);
}

/** ▶ у модуля — конструктор с фильтром «На сегодня», если есть что повторить или новые; иначе — весь модуль. */
export function trainPath(plan: TrainingModulePlan, place: 'words' | 'shelf'): string {
  const forToday = plan.repeat + plan.new > 0;
  trackEvent('training_module_chosen', { repeat: plan.repeat, new: plan.new, done_today: plan.doneToday, place });
  return buildCramPath(plan.moduleId) + (forToday ? `?cards=today&n=${TODAY_COUNT}` : '');
}
